/*
 * Suspense fallback for the lazy platform branch in main.tsx.
 *
 * This exists because that fallback was `null`, which is what produced the
 * reported black screen:
 *
 *   1. index.html paints the BOOT SEQUENCE loader inside #root.
 *   2. main.tsx runs, React mounts and REPLACES #root — the loader is gone.
 *   3. The lazy() import of DevourProviders + App (~1.5-2MB) is still in
 *      flight, so Suspense renders its fallback... which was `null`.
 *   4. Nothing is painted. The user sees the bare page background.
 *
 * In a browser that is a blank tab you reload on reflex. Inside the Electron
 * shell (frame: false — no address bar, no reload button) it is a solid black
 * window, and it repeats every launch because Electron re-serves the same chunk
 * from its own cache in userData.
 *
 * The markup below is a deliberate copy of index.html's static loader — same
 * class names, same nesting, same animation delays — so the handoff from static
 * HTML to React is invisible. index.html's <style> block is inline and always
 * present, so these classes are guaranteed to be styled. If you change one
 * side, change the other.
 */
function AppBootFallback(): JSX.Element {
    return (
        <div className="app-root-loader">
            <div className="app-boot" role="status" aria-live="polite" aria-busy="true">
                <span className="app-boot__eyebrow" aria-hidden="true">Boot Sequence</span>
                <div className="app-boot__title" aria-hidden="true">
                    {["L", "O", "A", "D", "I", "N", "G"].map((char, i) =>
                        <span
                            key={char}
                            className="app-boot__char"
                            style={{animationDelay: `${i * 90}ms`}}
                        >
                            {char}
                        </span>)}
                </div>
                <div className="app-boot__rule" aria-hidden="true">
                    <span className="app-boot__rule-fill"/>
                </div>
                <p className="app-boot__text">Booting DevourPlay&hellip;</p>
            </div>
        </div>
    );
}

export default AppBootFallback;
